import { useCallback, useMemo } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import type { AdminSearchParams } from "@/features/admin/types";
import type { SortField } from "../ClassificationRuleUtils";

export function useConfigurationSearchParams() {
  const search = useSearch({
    from: "/admin",
  }) as AdminSearchParams;
  const navigate = useNavigate({ from: "/admin" });

  const sorting = useMemo(() => {
    const sortField = (search.configSortBy as SortField) || "category";
    const sortDirection =
      (search.configSortDirection as "asc" | "desc") || "asc";
    return {
      sortField,
      sortDirection,
    };
  }, [search.configSortBy, search.configSortDirection]);

  const updateSearchParams = useCallback(
    (
      updates: Partial<{ sortField: SortField; sortDirection: "asc" | "desc" }>
    ) => {
      const newSearch: AdminSearchParams = { ...search };

      const sortField =
        updates.sortField ??
        (search.configSortBy as SortField | undefined) ??
        "category";
      const sortDirection =
        updates.sortDirection ??
        (search.configSortDirection as "asc" | "desc" | undefined) ??
        "asc";

      if (sortField === "category" && sortDirection === "asc") {
        delete newSearch.configSortBy;
        delete newSearch.configSortDirection;
      } else {
        newSearch.configSortBy = sortField;
        newSearch.configSortDirection = sortDirection;
      }

      (Object.keys(newSearch) as Array<keyof AdminSearchParams>).forEach(
        (key) => {
          if (newSearch[key] === undefined) {
            delete newSearch[key];
          }
        }
      );

      navigate({
        search: newSearch,
        replace: true,
      });
    },
    [search, navigate]
  );

  return {
    sorting,
    updateSearchParams,
  };
}
